import React, { useEffect, useMemo, useState } from 'react';
import { InputNumber, Input, Select, Switch, Tag } from 'antd';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeLoading, NdoeError,
} from '../../components/ndoe';
import ReculLivraisonSettings from './ReculLivraisonSettings';
import CreancesMigrationSettings from './CreancesMigrationSettings';
import { messageErreur } from '../../components/ndoe/chargement';
import './parametresBoutique.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Centre de paramétrage (Phase 1). Lit le schéma + valeurs via
// GET /api/parametre/settings, rend un contrôle typé par réglage, et enregistre
// chaque changement individuellement (PUT /settings/:cle, validé côté serveur).
const CATS = [
  { id: 'general', ic: '🏬', label: 'Général' },
  { id: 'reservations', ic: '🔖', label: 'Réservations' },
  { id: 'livraison', ic: '🚗', label: 'Livraison' },
  { id: 'finalisation', ic: '⏰', label: 'Rappels finalisation' },
  { id: 'livraison_recul', ic: '⏪', label: 'Antidatage livraison' },
  { id: 'couts', ic: '💰', label: 'Coûts' },
  { id: 'credit', ic: '💳', label: 'Crédit & créances' },
  { id: 'inventaire', ic: '📦', label: 'Inventaire' },
  { id: 'regularisation', ic: '🗓️', label: 'Régularisation' },
  { id: 'creances_migration', ic: '🧭', label: 'Migration créances' },
];
const ROLE_OPTS = [
  { value: 'admin', label: 'Admin' }, { value: 'secretaire', label: 'Secrétaire' },
  { value: 'vendeur', label: 'Vendeur' }, { value: 'livreur', label: 'Livreur' },
];

const ParametresBoutique = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [cat, setCat] = useState('general');
  const [draft, setDraft] = useState({});      // cle -> valeur en cours d'édition
  const [saving, setSaving] = useState({});     // cle -> bool

  const charger = async () => {
    setLoading(true); setErreur(null);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/parametre/settings`);
      const list = Array.isArray(data?.settings) ? data.settings : [];
      setItems(list);
      const d = {};
      list.forEach((s) => { d[s.cle] = s.valeur; });
      setDraft(d);
    } catch (e) {
      setErreur(e.response?.data?.error || messageErreur(e, 'les réglages de la boutique'));
    } finally { setLoading(false); }
  };
  useEffect(() => { charger(); }, []);

  const parCat = useMemo(() => items.filter((s) => s.categorie === cat), [items, cat]);
  const modifie = (s) => String(draft[s.cle] ?? '') !== String(s.valeur ?? '');

  const enregistrer = async (s) => {
    if (saving[s.cle]) return;
    setSaving((m) => ({ ...m, [s.cle]: true }));
    try {
      const { data } = await axios.put(`${DOMAIN}/api/parametre/settings/${s.cle}`, { valeur: draft[s.cle] });
      setItems((arr) => arr.map((x) => (x.cle === s.cle ? { ...x, valeur: data.valeur } : x)));
      setDraft((m) => ({ ...m, [s.cle]: data.valeur }));
      toast.success(`« ${s.libelle} » enregistré.`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enregistrement impossible.');
    } finally { setSaving((m) => ({ ...m, [s.cle]: false })); }
  };

  const controle = (s) => {
    const v = draft[s.cle];
    const set = (val) => setDraft((m) => ({ ...m, [s.cle]: val }));
    if (s.readonly) {
      return <Tag>{v || '—'} <span style={{ opacity: 0.6 }}>· via permissions (Phase 2)</span></Tag>;
    }
    switch (s.type) {
      case 'int':
        return <InputNumber inputMode="decimal" min={s.min} max={s.max} value={v === '' ? null : Number(v)} onChange={(n) => set(n == null ? '' : String(n))} addonAfter={s.unite} />;
      case 'floatOrEmpty':
        return <InputNumber inputMode="decimal" min={s.min} max={s.max} value={v === '' ? null : Number(v)} onChange={(n) => set(n == null ? '' : String(n))} addonAfter={s.unite} placeholder="désactivé" />;
      case 'bool':
        return <Switch checked={v === '1' || v === 1 || v === true} onChange={(c) => set(c ? '1' : '0')} />;
      case 'time':
        return <Input style={{ maxWidth: 120 }} value={v} onChange={(e) => set(e.target.value)} placeholder="HH:MM" />;
      case 'timelist':
        return <Input style={{ maxWidth: 320 }} value={v} onChange={(e) => set(e.target.value)} placeholder="18:00,20:00,22:00,23:30" />;
      case 'enum':
        return <Select style={{ minWidth: 200 }} value={v} onChange={set} options={(s.options || []).map((o) => ({ value: o, label: o }))} />;
      case 'roles':
        return <Select mode="multiple" style={{ minWidth: 240 }} value={(v || '').split(',').filter(Boolean)} onChange={(arr) => set(arr.join(','))} options={ROLE_OPTS} />;
      case 'digits':
        return <Input style={{ maxWidth: 140 }} value={v} onChange={(e) => set(e.target.value.replace(/\D/g, ''))} />;
      case 'json':
        return <Input.TextArea rows={3} style={{ maxWidth: 420, fontFamily: 'Consolas, monospace' }} value={v} onChange={(e) => set(e.target.value)} placeholder='[{"code":"casse","label":"Casse"}] — vide = 6 motifs par défaut' />;
      default:
        return <Input style={{ maxWidth: 360 }} value={v} onChange={(e) => set(e.target.value)} maxLength={s.max} />;
    }
  };

  return (
    <NdoePage className="pb-page">
      <NdoePageHeader
        eyebrow="Administration"
        title="Paramètres de la boutique"
        subtitle="Les réglages qui pilotent le fonctionnement de l'app, réunis ici. Chaque modification est enregistrée individuellement et validée."
      />

      <div className="pb-tabs" role="tablist">
        {CATS.map((c) => (
          <button key={c.id} type="button" role="tab" aria-selected={cat === c.id}
            className={`pb-tab ${cat === c.id ? 'on' : ''}`} onClick={() => setCat(c.id)}>
            <span className="pb-ic">{c.ic}</span>{c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <NdoeLoading rows={5} />
      ) : erreur ? (
        <NdoeError text={erreur} onRetry={charger} />
      ) : cat === 'livraison_recul' ? (
        <ReculLivraisonSettings
          items={items}
          onSaved={(cle, valeur) => {
            setItems((arr) => arr.map((x) => (x.cle === cle ? { ...x, valeur } : x)));
            setDraft((m) => ({ ...m, [cle]: valeur }));
          }}
        />
      ) : cat === 'creances_migration' ? (
        <CreancesMigrationSettings
          items={items}
          onSaved={(cle, valeur) => {
            setItems((arr) => arr.map((x) => (x.cle === cle ? { ...x, valeur } : x)));
            setDraft((m) => ({ ...m, [cle]: valeur }));
          }}
        />
      ) : (
        <div className="pb-group ndoe-card">
          {parCat.map((s) => (
            <div className="pb-row" key={s.cle}>
              <div className="pb-lab">
                <b>{s.libelle}</b>
                {s.aide ? <small>{s.aide}</small> : null}
                <code className="pb-cle">{s.cle}</code>
              </div>
              <div className="pb-ctl">
                {controle(s)}
                {modifie(s) && !s.readonly && (
                  <NdoeActionButton size="sm" variant="primary" loading={!!saving[s.cle]} onClick={() => enregistrer(s)}>
                    Enregistrer
                  </NdoeActionButton>
                )}
              </div>
            </div>
          ))}
          {!parCat.length && <div style={{ padding: 20, color: 'var(--ndoe-muted)' }}>Aucun réglage dans cette catégorie.</div>}
        </div>
      )}
    </NdoePage>
  );
};

export default ParametresBoutique;
